import { LoadingIndicator } from '#/components/common/loading-state'
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowRight, CheckCircle2, GitMerge, Hammer, Loader2, MessageCircleQuestion, PenLine, Rocket, UploadCloud } from 'lucide-react'
import { Button } from '#/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '#/components/ui/tooltip'
import { ApiError } from '#/lib/api'
import { useFireNextAction, useNextAction, type NextActionDto } from '#/lib/api-inbox'
import { cn } from '#/lib/utils'
import type { ExecutionMode } from '#/lib/types'
import { m } from '#/paraglide/messages'

// Orkestrator E1: `team.*` keys are additive — looked up by name so an unknown key shows itself instead of crashing.
const LABELS: Record<string, () => string> = {
  'plan.write': m.inbox_na_plan_write,
  'plan.compose': m.inbox_na_plan_compose,
  'plan.generating': m.inbox_na_plan_generating,
  'phase.approve': m.inbox_na_phase_approve,
  'run.answer': m.inbox_na_run_answer,
  'run.wait': m.inbox_na_run_wait,
  'proposal.review': m.inbox_na_proposal_review,
  'run.next': m.inbox_na_run_next,
  'task.next': m.inbox_na_task_next,
  'task.resolve': m.audit_resolve_steps,
  publish: m.inbox_na_publish,
  'publish.update': m.inbox_na_publish_update,
  idle: m.inbox_na_idle,
  'team.conflict': m.inbox_na_team_conflict,
  'team.approve': m.inbox_na_team_approve,
  'team.answer': m.inbox_na_team_answer,
  'team.wait': m.inbox_na_team_wait,
}
const label = (k: NextActionDto['key']) => LABELS[k]?.() ?? k

const detailOf = (a: NextActionDto) =>
  a.key === 'phase.approve' && a.detail.phase
    ? m.inbox_na_detail_phase({ n: a.detail.phase })
    : a.key === 'idle'
      ? m.inbox_na_detail_idle()
      : a.detail.todo > 0
        ? m.inbox_na_detail_left({ n: a.detail.todo })
        : a.detail.total > 0
          ? m.inbox_na_detail_done({ done: a.detail.done, total: a.detail.total })
          : ''

function Icon({ k, className }: { k: NextActionDto['key']; className?: string }) {
  const key: string = k
  if (key === 'plan.write' || key === 'plan.compose') return <PenLine className={className} />
  if (key === 'plan.generating' || key === 'run.wait' || key === 'team.wait') return <Loader2 className={cn('animate-spin', className)} />
  if (key === 'phase.approve' || key === 'proposal.review' || key === 'team.approve') return <CheckCircle2 className={className} />
  if (key === 'run.answer' || key === 'team.answer') return <MessageCircleQuestion className={className} />
  if (key === 'team.conflict') return <GitMerge className={className} />
  if (key === 'run.next' || key === 'task.next') return <Hammer className={className} />
  if (key === 'publish') return <UploadCloud className={className} />
  if (key === 'publish.update') return <Rocket className={className} />
  return <ArrowRight className={className} />
}

/** Zuse-style label for the execution mode, in human words (Inspirasi F2 — enum untouched). */
export const execModeLabel = (mode: ExecutionMode) => (mode === 'autopilot' ? m.inbox_exec_autopilot() : mode === 'strict' ? m.inbox_exec_strict() : m.inbox_exec_checkpoint())

/**
 * The one button whose label follows the project's state (Inspirasi F2, after Zuse's workflow bar).
 * `size="lg"` = the big button on /bangun and the home cards; `size="sm"` = the compact header one.
 * When the action can run in place (`run.next`) it POSTs; otherwise it navigates.
 */
export function PrimaryAction({
  planId,
  mode,
  size = 'lg',
  execMode,
  className,
  onFired,
}: {
  planId: string | null | undefined
  mode: 'dev' | 'simple'
  size?: 'lg' | 'sm'
  /** shown as a tooltip in human words when given */
  execMode?: ExecutionMode | null
  className?: string
  onFired?: () => void
}) {
  const { data: a, isLoading, isError, refetch, isFetching } = useNextAction(planId, mode)
  const fire = useFireNextAction()
  const [note, setNote] = useState<string | null>(null)
  if (!planId) return null
  if (isError && !a) return <Button size="sm" variant="outline" loading={isFetching} onClick={() => void refetch()}>{m.brand_retry()}</Button>
  if (isLoading || !a) {
    return (
      <span className={cn('inline-flex items-center gap-2 text-xs text-muted-foreground', className)} data-testid="primary-action-loading">
        <LoadingIndicator label={m.inbox_loading()} className="text-xs" />
      </span>
    )
  }
  const big = size === 'lg'
  const inner = (
    <>
      <Icon k={a.key} className={big ? 'size-4' : 'size-3.5'} />
      {label(a.key)}
    </>
  )
  const detail = detailOf(a)
  const btnClass = cn(big ? 'h-10 gap-2 px-4 text-sm' : 'h-8 gap-1.5 px-2.5 text-xs', a.state === 'blocked' && 'ring-2 ring-status-progress/40')
  const button =
    a.action && !a.disabled ? (
      <Button aria-busy={fire.isPending}
        size={big ? 'default' : 'sm'}
        className={btnClass}
        disabled={fire.isPending}
        data-testid="primary-action"
        data-key={a.key}
        onClick={() => {
          setNote(null)
          fire.mutate(a.action!, {
            onSuccess: () => {
              setNote(m.inbox_na_started())
              onFired?.()
            },
            onError: (e) => setNote(e instanceof ApiError ? e.message : m.inbox_na_error()),
          })
        }}
      >
        {fire.isPending ? <Loader2 className="size-4 animate-spin" /> : inner}
      </Button>
    ) : (
      <Button asChild size={big ? 'default' : 'sm'} className={btnClass} disabled={a.disabled} data-testid="primary-action" data-key={a.key} variant={a.key === 'idle' ? 'outline' : 'default'}>
        <Link to={a.href as '/'} aria-disabled={a.disabled}>
          {inner}
        </Link>
      </Button>
    )
  const wrapped = execMode ? (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="bottom">
        {m.inbox_exec_hint()}: {execModeLabel(execMode)}
      </TooltipContent>
    </Tooltip>
  ) : (
    button
  )
  if (!big) return <span className={cn('inline-flex items-center', className)}>{wrapped}</span>
  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1', className)}>
      {wrapped}
      {note ? <span className="text-xs text-muted-foreground">{note}</span> : detail ? <span className="text-xs text-muted-foreground">{detail}</span> : null}
    </div>
  )
}
